(ns app.front.view.property.dashboard.widgets.new-property
  (:require [app.domain.property.model :as property-model]
            [app.front.ui :as ui]
            [app.front.ui.add-widget :as ui-add]
            [app.front.ui.select :as ui-select]
            [app.front.util :as futil]
            [app.front.view.property.dashboard.subs :as property-dashboard-subs]
            [clojure.string :as str]
            [core.front.event :refer [<sub >evt]]
            [core.front.feature-flags :as ff]
            [core.util.string :as str-util]
            [core.util.util :as util]
            [potpuri.core :as p]
            [re-frame.core-instrumented :as rf]))

;; Events
(rf/reg-event-fx ::create-new-property-record
  (fn [_ [_ {:keys [domain-group municipality address id]}]]
    {:dispatch [:comm/command-monitored
                {:command        :property/create
                 :data           (p/assoc-if {:domain-group-id (:db/id domain-group)
                                              :municipality-id (:db/id municipality)}
                                             :address (str-util/blank->nil address)
                                             :property-id (str-util/blank->nil id))
                 :on-success     [::create-new-property-record.success]
                 :on-bad-request [::create-new-property-record.bad-request
                                  {:address (str-util/blank->nil address)}]}]}))

(rf/reg-event-fx ::create-new-property-record.success
  (fn [_ [_ _ {:keys [property-id]}]]
    {:dispatch   [::reset-state]
     :route/push {:route :view/property :params {:db-id property-id}}}))

(rf/reg-event-fx ::create-new-property-record.bad-request
  (fn [{:keys [db]} [_ {:keys [address]} status {:keys [error existing-property-id] :as body} request-id]]
    (if (and (ff/enabled? :feature-flag.id/property-multiple-addresses)
             (= error :error/property-exists)
             existing-property-id
             address)
      {:db (assoc-in db [::state :add-conflicting-address] {:property-id existing-property-id
                                                            :address     {:address/full-address address}})}
      {:dispatch [:comm/add-error-notification status body request-id]})))

(rf/reg-event-fx ::add-conflicting-address
  (fn [{:keys [db]} [_ {:keys [property-id address]}]]
    {:db       (update db ::state dissoc :add-conflicting-address)
     :dispatch [:comm/command-monitored
                {:command    :property/add-address
                 :method     :post
                 :data       {:property-id property-id
                              :address     address}
                 :on-success [::add-conflicting-address.success property-id]}]}))

(rf/reg-event-fx ::add-conflicting-address.success
  (fn [_ [_ property-id _ _]]
    {:fx [[:dispatch [::reset-state]]
          [:dispatch [:notifications/add :toast :success :success :property-view/address-added-successfully]]
          [:route/push {:route  :view/property
                        :params {:db-id property-id}}]]}))

(rf/reg-event-db ::set-add-conflicting-address
  (rf/path [::state])
  (fn [db [_ value]]
    (assoc db :add-conflicting-address value)))

;; Db events
(rf/reg-event-db ::reset-state
  (fn [db _]
    (dissoc db ::state)))

(rf/reg-event-db ::set-domain-group
  (rf/path [::state])
  (fn [db [_ domain-group]]
    (-> db
        (dissoc :municipality)
        (assoc :domain-group domain-group))))

(rf/reg-event-db ::set-municipality
  (rf/path [::state])
  (fn [db [_ municipality]]
    (assoc db :municipality municipality)))

(rf/reg-event-db ::set-address
  (rf/path [::state])
  (fn [db [_ address]]
    (assoc db :address address)))

(rf/reg-event-db ::set-id
  (rf/path [::state])
  (fn [db [_ id]]
    (assoc db :id id)))

;; Subscriptions
(rf/reg-sub ::state-with-defaults
  :<- [::property-dashboard-subs/default-domain-group]
  :<- [::state]
  (fn [[default-dg state] _]
    (-> state
        (update :domain-group (fnil identity default-dg))
        ((fn [state-with-dg]
           (let [default-muni (-> state-with-dg :domain-group :municipalities first)]
             (update state-with-dg :municipality (fnil identity default-muni))))))))

(rf/reg-sub ::state :-> ::state)

(rf/reg-sub ::domain-group
  :<- [::state-with-defaults]
  :-> :domain-group)

(rf/reg-sub ::municipality
  :<- [::state-with-defaults]
  :-> :municipality)

(rf/reg-sub ::address
  :<- [::state-with-defaults]
  :-> :address)

(rf/reg-sub ::id
  :<- [::state-with-defaults]
  :-> :id)

(rf/reg-sub ::add-conflicting-address
  :<- [::state]
  :-> :add-conflicting-address)

(rf/reg-sub ::draft?
  :<- [::address]
  :<- [::id]
  (fn [fields _]
    (->> fields
         (some str/blank?)
         boolean)))

;; Components
(defn- widget-buttons [tr]
  [{:text      (if (<sub [::draft?])
                 (tr [:property/create-draft-record])
                 (tr [:create]))
    :disabled? (or (nil? (<sub [::domain-group]))
                   (nil? (<sub [::municipality])))
    :on-click  #(>evt [::create-new-property-record (<sub [::state-with-defaults])])}])

(defn- domain-group-selector [tr]
  [ui-select/select {:id           "new-property-domain-group-selector"
                     :class        "full-width-select"
                     :allow-empty? false
                     :label-text   (tr [:property/select-domain-group])
                     :on-change    #(>evt [::set-domain-group %])
                     :required     true
                     :val->str     :group/name
                     :value        (<sub [::domain-group])
                     :values       (<sub [::property-dashboard-subs/domain-groups])}])

(defn- municipality-selector [tr]
  [ui-select/select {:id           "new-property-municipality-selector"
                     :class        "full-width-select"
                     :allow-empty? false
                     :label-text   (tr [:municipalities/select-municipality])
                     :on-change    #(>evt [::set-municipality %])
                     :required     true
                     :val->str     (fn [municipality]
                                     (->> municipality
                                          :municipality/names
                                          (filter #(-> % :entity/language (= (<sub [:language]))))
                                          first
                                          :localized-string/text))
                     :value        (<sub [::municipality])
                     :values       (<sub [::property-dashboard-subs/municipalities (<sub [::domain-group])])}])

(defn- address-field [tr]
  [ui/text-input {:id         "new-property-record-address"
                  :label-text (tr [:property/new-property-record-address-text])
                  :on-change  #(>evt [::set-address (-> % futil/tval)])
                  :value      (<sub [::address])}])

(defn- id-field [tr]
  [ui/text-input {:id         "new-property-record-id"
                  :label-text (tr [:property/new-property-record-id-text]
                                  [(tr [:property/id])])
                  :on-change  #(>evt [::set-id (-> % futil/tval)])
                  :value      (<sub [::id])}])

(defn view [default-tr]
  (let [selected-domain-group    (<sub [::domain-group])
        tr                       (property-model/property-tr-fn default-tr selected-domain-group)
        selected-municipality    (<sub [::municipality])
        multiple-municipalities? (-> selected-domain-group :municipalities count (> 1))
        add-conflicting-address  (<sub [::add-conflicting-address])
        multiple-addr?           (<sub [::ff/enabled? :feature-flag.id/property-multiple-addresses])]
    [ui-add/standalone-add-widget
     (util/mmap {:buttons  (widget-buttons tr)
                 :title    (tr [:property/create-property-record])
                 :loading? (or (<sub [:comm/command-processing? :property/create])
                               (<sub [:comm/command-processing? :property/add-address])
                               (<sub [:comm/command-processing? :property-management/creation-data]))
                 :on-close #(>evt [::property-dashboard-subs/toggle-new-property-visibility])
                 :help     (tr [:property/create-property-record-help]
                               [(tr [:property/id])])}
                tr)
     (if (<sub [::property-dashboard-subs/user-has-any-property-access?])
       [:div.grid.md:grid-cols-2.gap-4
        (when (<sub [::property-dashboard-subs/multiple-domain-groups?])
          [domain-group-selector tr])

        (when multiple-municipalities?
          [municipality-selector tr])

        (when (and selected-domain-group
                   selected-municipality)
          [:<>
           [:div.col-start-1
            [address-field tr]]
           [id-field tr]])]
       [:h3 (tr [:property/no-available-domain-groups])])
     (when multiple-addr?
       [ui/confirm-modal
        {:tr              tr
         :id              "add-conflicting-property-address"
         :modal-title     (tr [:property/add-address-to-existing-property])
         :open?           (boolean add-conflicting-address)
         :on-close        #(>evt [::set-add-conflicting-address nil])
         :on-yes-click    #(>evt [::add-conflicting-address add-conflicting-address])
         :yes-button-text (tr [:property/add-address])}
        (tr [:property/add-address-to-existing-property-confirm] [(tr [:property/id])])])]))
